import React, { useState, useEffect, useMemo } from 'react';
import { adminServices } from '../services/adminServices';
import { WORLD_BANK_TIERS, DEFAULT_MULTIPLIERS } from '../locales/economicData';
import { countryEndonyms } from '../locales';
import { useUser } from '../UserContext';
import AdminOffersSection from './AdminOffersSection';
import {
  BILLABLE_LEVELS,
  MODE_LABELS,
  REGISTRATION_MODES,
  computeFeeGbp,
  summarizeCountryRow,
} from '../services/pricingRules';

const MODE_OPTIONS = [
  { value: REGISTRATION_MODES.ONLINE, label: MODE_LABELS.online, color: '#138a8a' },
  { value: REGISTRATION_MODES.PARTNER, label: MODE_LABELS.partner, color: '#d97706' },
  { value: REGISTRATION_MODES.CLOSED, label: MODE_LABELS.closed, color: '#d11d7a' },
];

const AdminPricing = () => {
  const { user, permissions } = useUser();
  const [config, setConfig] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({ GBP: 1 });
  const [currency, setCurrency] = useState('GBP');
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [scalingModal, setScalingModal] = useState(null);
  const [countryOverridesOpen, setCountryOverridesOpen] = useState(false);
  const [localBase, setLocalBase] = useState({});
  const [saveFeedback, setSaveFeedback] = useState(null); // { type: 'success'|'error', message: string }

  useEffect(() => {
    const load = async () => {
      const data = await adminServices.getPricingConfig();
      setConfig(data);
      setLocalBase(data.baseRates);
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/GBP');
        const json = await res.json();
        setExchangeRates(json.rates);
      } catch (err) { console.error("Currency fetch failed"); }
    };
    load();
  }, []);

  const canEdit = !!permissions.canManagePayments;

  const convert = (val) => {
    const rate = exchangeRates[currency] || 1;
    return (val * rate).toFixed(2);
  };

  const deconvert = (val) => {
    const rate = exchangeRates[currency] || 1;
    return Number(val) / rate;
  };

  // --- LOGIC: CASCADING UPDATES ---
  
  const commitBaseChange = (level) => {
    const newVal = Number(localBase[level]);
    if (newVal === config.baseRates[level]) return;
    setScalingModal({ level, oldVal: config.baseRates[level], newVal });
  };

  const handleScalingChoice = (choice) => {
    const { level, oldVal, newVal } = scalingModal;
    let newTierOverrides = { ...(config.tierOverrides || {}) };
    let newCountryOverrides = { ...(config.countryOverrides || {}) };

    if (choice === 'RESET') {
      // Wipe all related overrides
      Object.keys(newTierOverrides).forEach(t => {
        if (newTierOverrides[t][level]) delete newTierOverrides[t][level];
      });
      Object.keys(newCountryOverrides).forEach(c => {
        if (newCountryOverrides[c][level]) delete newCountryOverrides[c][level];
      });
    } else if (choice === 'SCALE') {
      const ratio = newVal / oldVal;
      // Scale Tier Overrides
      Object.keys(newTierOverrides).forEach(t => {
        if (newTierOverrides[t][level]) newTierOverrides[t][level] = Math.round(newTierOverrides[t][level] * ratio);
      });
      // Scale Country Overrides
      Object.keys(newCountryOverrides).forEach(c => {
        if (newCountryOverrides[c][level]) newCountryOverrides[c][level] = Math.round(newCountryOverrides[c][level] * ratio);
      });
    }

    setConfig({
      ...config,
      baseRates: { ...config.baseRates, [level]: newVal },
      tierOverrides: newTierOverrides,
      countryOverrides: newCountryOverrides
    });
    setScalingModal(null);
  };

  const updateTierRate = (tier, level, val) => {
    const to = config.tierOverrides || {};
    const newTierOverrides = { ...to, [tier]: { ...(to[tier] || {}), [level]: Math.round(val) } };
    
    // Rule: Any change to a tier automatically overrides/clears individual country tweaks in that tier
    const newCountryOverrides = { ...(config.countryOverrides || {}) };
    Object.keys(newCountryOverrides).forEach(country => {
      if (WORLD_BANK_TIERS[country] === Number(tier)) {
        if (newCountryOverrides[country]?.[level]) delete newCountryOverrides[country][level];
      }
    });

    setConfig({ ...config, tierOverrides: newTierOverrides, countryOverrides: newCountryOverrides });
  };

  const updateCountryRate = (country, level, val) => {
    const co = config.countryOverrides || {};
    setConfig({
      ...config,
      countryOverrides: {
        ...co,
        [country]: { ...(co[country] || {}), [level]: Math.round(val) }
      }
    });
  };

  const resetOverride = (type, id, level) => {
    if (type === 'tier') {
      const newT = { ...(config.tierOverrides || {}) };
      if (newT[id]) delete newT[id][level];
      setConfig({ ...config, tierOverrides: newT });
    } else {
      const newC = { ...(config.countryOverrides || {}) };
      if (newC[id]) delete newC[id][level];
      setConfig({ ...config, countryOverrides: newC });
    }
  };

  const updateCountryRule = (country, level, patch) => {
    const rules = { ...(config.countryRules || {}) };
    const row = { ...(rules[country] || {}) };
    const cell = { ...(row[level] || { mode: REGISTRATION_MODES.CLOSED, feeGbp: null, partnerName: '' }) };
    row[level] = { ...cell, ...patch };
    rules[country] = row;
    setConfig({ ...config, countryRules: rules, schemaVersion: 2 });
  };

  const updateCountryRuleFee = (country, level, val) => {
    const rounded = Math.round(Number(val));
    const rules = { ...(config.countryRules || {}) };
    const co = { ...(config.countryOverrides || {}) };
    const row = { ...(rules[country] || {}) };
    const cell = { ...(row[level] || {}) };
    row[level] = { ...cell, feeGbp: rounded };
    rules[country] = row;
    co[country] = { ...(co[country] || {}), [level]: rounded };
    setConfig({ ...config, countryRules: rules, countryOverrides: co, schemaVersion: 2 });
  };

  const resetCountryRuleFee = (country, level) => {
    const rules = { ...(config.countryRules || {}) };
    const co = { ...(config.countryOverrides || {}) };
    if (rules[country]) {
      const row = { ...rules[country] };
      const cell = { ...(row[level] || {}) };
      delete cell.feeGbp;
      row[level] = cell;
      rules[country] = row;
    }
    if (co[country]?.[level] != null) {
      const crow = { ...co[country] };
      delete crow[level];
      if (Object.keys(crow).length) co[country] = crow;
      else delete co[country];
    }
    setConfig({ ...config, countryRules: rules, countryOverrides: co });
  };

  const getCellFee = (country, level) => {
    const cell = config.countryRules?.[country]?.[level];
    if (cell?.feeGbp != null && Number.isFinite(Number(cell.feeGbp))) {
      return Math.round(Number(cell.feeGbp));
    }
    return computeFeeGbp(country, level, config);
  };

  const toggleRegistrationsPaused = () => {
    const next = !(config.registrationsPaused === true);
    setConfig({
      ...config,
      registrationsPaused: next,
      globalMarketsDisabled: next,
    });
  };

  const filteredCountries = useMemo(() => {
    return countryEndonyms.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const handleSave = async () => {
    setSaveFeedback(null);
    setIsSaving(true);
    try {
      const { auditLogged } = await adminServices.savePricingConfig(
        config,
        user?.displayName || user?.email || 'IFIP Staff'
      );
      if (auditLogged === false) {
        setSaveFeedback({
          type: 'error',
          message:
            'Fees saved to Firestore, but the System Logs entry failed (see browser console). Common cause: invalid log payload; try again after a refresh.',
        });
      } else {
        setSaveFeedback({
          type: 'success',
          message:
            'Fees and country availability published to Firestore (settings/pricing + public/fees for ifip.group). Open System Logs — you should see PRICING_UPDATE at the top.',
        });
      }
    } catch (e) {
      console.error('savePricingConfig', e);
      setSaveFeedback({
        type: 'error',
        message: e?.message || 'Save failed. Check the browser console and your Firestore rules.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!config) return <div className="admin-placeholder">Loading Financial Matrix...</div>;

  return (
    <div className="admin-pricing-view fade-in">
      
      {/* 1. HEADER & CURRENCY */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h3 style={{ color: 'white', margin: 0 }}>Manage Fees and Offers — Fee engine (GBP)</h3>
          <p style={{ fontSize: '0.8rem', opacity: 0.6 }}>Manage cascading World Bank tiers and specific country overrides.</p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="admin-select" style={{ width: '100px', background: '#0f172a', border: '1px solid #334155', color: 'white', borderRadius: '4px' }}>
            {Object.keys(exchangeRates).map(curr => <option key={curr} value={curr}>{curr}</option>)}
          </select>
          <button onClick={handleSave} disabled={!canEdit || isSaving} className="btn-activate">
            {isSaving ? "Syncing..." : "Save & Publish Changes"}
          </button>
        </div>
        {saveFeedback && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '0.8rem',
              border: saveFeedback.type === 'error' ? '1px solid rgba(248,113,113,0.5)' : '1px solid rgba(45,212,191,0.4)',
              background: saveFeedback.type === 'error' ? 'rgba(127,29,29,0.35)' : 'rgba(6,78,59,0.35)',
              color: saveFeedback.type === 'error' ? '#fecaca' : '#a7f3d0',
            }}
          >
            {saveFeedback.message}
          </div>
        )}
      </header>

      {/* 2. TIER GRID */}
      <section className="pricing-section">
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 14px 0', lineHeight: 1.45, maxWidth: '900px' }}>
          <strong style={{ color: '#cbd5e1' }}>Tier row edits can clear country fees:</strong> changing a <strong>Tier 2–4</strong> cell removes saved <strong>country overrides</strong> for that tier and level. Changing <strong>base rates</strong> and choosing <strong>Reset</strong> in the popup removes <strong>all</strong> country overrides for that level. Country-specific amounts are saved only when you click <strong>Save &amp; Publish Changes</strong>.
        </p>
        <table className="pricing-table">
          <thead>
            <tr>
              <th>Base Rate (Tier 1)</th>
              <th>Foundation</th>
              <th>Certified</th>
              <th>Accredited</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4].map(tier => (
              <tr key={tier}>
                <td style={{ fontWeight: 'bold' }}>
                    Tier {tier} 
                    <span style={{ fontSize: '0.6rem', opacity: 0.5, marginLeft: '10px' }}>
                        {tier === 1 ? 'BASE' : `(${Math.round(DEFAULT_MULTIPLIERS[tier] * 100)}%)`}
                    </span>
                </td>
                {['foundation', 'certified', 'accredited'].map(level => {
                  const isBase = tier === 1;
                  const isOverridden = !isBase && (config.tierOverrides || {})[tier]?.[level];
                  const val = isBase
                    ? config.baseRates?.[level]
                    : (isOverridden || Math.round((config.baseRates?.[level] || 0) * DEFAULT_MULTIPLIERS[tier]));
                  
                  return (
                    <td key={level} className={isOverridden ? 'cell-overridden' : ''}>
                      <div className="input-with-reset">
                        <span className="currency-symbol">{currency}</span>
                        <input 
                          type="number" 
                          value={isBase ? convert(localBase[level]) : convert(val)} 
                          onChange={(e) => {
                            const v = deconvert(e.target.value);
                            if (isBase) setLocalBase({ ...localBase, [level]: v });
                            else updateTierRate(tier, level, v);
                          }}
                          onKeyDown={(e) => isBase && e.key === 'Enter' && commitBaseChange(level)}
                          className="pricing-input"
                          disabled={!canEdit}
                        />
                        {isBase && Number(localBase[level]) !== Number(config.baseRates[level]) && (
                          <button onClick={() => commitBaseChange(level)} className="btn-commit" title="Commit Change">✓</button>
                        )}
                        {isOverridden && <button onClick={() => resetOverride('tier', tier, level)} className="btn-reset">↺</button>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <AdminOffersSection canEdit={canEdit} adminName={user?.displayName || user?.email} />

      {/* 3. COUNTRY MATRIX (collapsible) */}
      <section className="pricing-section pricing-section--accordion" style={{ marginTop: '40px' }}>
        <button
          type="button"
          onClick={() => setCountryOverridesOpen((open) => !open)}
          className="pricing-accordion-trigger"
          aria-expanded={countryOverridesOpen}
        >
          <span style={{ color: '#138a8a', fontWeight: 700, fontSize: '1rem' }}>Country registration rules</span>
          <span style={{ fontSize: '0.8rem', opacity: 0.55, color: '#94a3b8' }}>
            {filteredCountries.length} countries
          </span>
          <span style={{ marginLeft: 'auto', fontSize: '0.85rem', opacity: 0.7 }} aria-hidden>
            {countryOverridesOpen ? 'Hide' : 'Show'}
          </span>
        </button>

        {countryOverridesOpen && (
        <>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '16px 0 20px 0', lineHeight: 1.55, maxWidth: '920px' }}>
          <strong style={{ color: '#cbd5e1' }}>Defaults (schema v2):</strong> Foundation = <strong>Online (Stripe)</strong>;
          Certified and Global = <strong>Not offered</strong> until you open each market.
          Set fee and registration mode per cell. <strong>Partner code</strong> = school pays locally first, then enters a receipt code from Offer codes below.
        </p>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '20px',
        }}>
          <button
            type="button"
            onClick={toggleRegistrationsPaused}
            disabled={!canEdit}
            className="btn-nudge"
            style={{
              background: config.registrationsPaused ? '#d11d7a' : 'rgba(255,255,255,0.12)',
              fontSize: '0.72rem',
              border: config.registrationsPaused ? '1px solid #f472b6' : '1px solid #475569',
              color: 'white',
              padding: '8px 14px',
              borderRadius: '6px',
              cursor: canEdit ? 'pointer' : 'not-allowed',
              fontWeight: 700,
            }}
            title="Emergency only — blocks all new registrations until turned off"
          >
            {config.registrationsPaused ? 'New registrations: PAUSED' : 'Pause all new registrations'}
          </button>
          <input
            type="text"
            placeholder="Search countries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-search"
            style={{
              marginLeft: 'auto',
              width: '220px', background: '#0f172a', border: '1px solid #334155',
              color: 'white', padding: '10px 15px', borderRadius: '6px', fontSize: '0.85rem',
            }}
          />
        </div>
        {config.registrationsPaused && (
          <div style={{
            marginBottom: '14px', padding: '12px 14px', borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.45)', background: 'rgba(127, 29, 29, 0.28)',
            fontSize: '0.8rem', lineHeight: 1.5, color: '#fecaca',
          }}>
            <strong>Emergency pause active</strong> — no new registrations until you turn this off. Country rules below are preserved.
          </div>
        )}
        <div className="country-scroll-container" style={{ maxHeight: '560px', overflowY: 'auto' }}>
          <table className="pricing-table country-rules-table">
            <thead>
              <tr>
                <th style={{ width: '220px' }}>Country</th>
                <th>Foundation</th>
                <th>Certified</th>
                <th>Global</th>
              </tr>
            </thead>
            <tbody>
              {filteredCountries.map((country) => {
                const rowSummary = summarizeCountryRow(country, config, config.countryRules);
                return (
                <tr key={country}>
                  <td style={{ fontSize: '0.82rem', verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 600 }}>{country}</div>
                    <span className="tier-tag">T{WORLD_BANK_TIERS[country] || 1}</span>
                    <div style={{ fontSize: '0.62rem', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {rowSummary}
                    </div>
                  </td>
                  {BILLABLE_LEVELS.map((level) => {
                    const cell = config.countryRules?.[country]?.[level] || { mode: REGISTRATION_MODES.CLOSED, feeGbp: null };
                    const mode = cell.mode || REGISTRATION_MODES.CLOSED;
                    const fee = getCellFee(country, level);
                    const isFeeOverridden = cell.feeGbp != null && Number.isFinite(Number(cell.feeGbp));
                    const modeMeta = MODE_OPTIONS.find((o) => o.value === mode) || MODE_OPTIONS[2];
                    return (
                      <td key={level} style={{ verticalAlign: 'top', minWidth: '168px' }}>
                        <div
                          className="country-rule-cell"
                          style={{
                            border: `1px solid ${modeMeta.color}`,
                            borderRadius: '8px',
                            padding: '8px',
                            background: mode === REGISTRATION_MODES.CLOSED ? 'rgba(209,29,122,0.06)' : 'rgba(0,0,0,0.2)',
                            opacity: mode === REGISTRATION_MODES.CLOSED ? 0.85 : 1,
                          }}
                        >
                          <div className="input-with-reset" style={{ marginBottom: '8px', border: '1px solid #334155' }}>
                            <span className="currency-symbol">{currency}</span>
                            <input
                              type="number"
                              value={convert(fee)}
                              style={{ textDecoration: mode === REGISTRATION_MODES.CLOSED ? 'line-through' : 'none' }}
                              onChange={(e) => updateCountryRuleFee(country, level, deconvert(e.target.value))}
                              className="pricing-input"
                              disabled={!canEdit}
                              title="Fee in GBP (override). Leave tier default by reset."
                            />
                            {isFeeOverridden && (
                              <button type="button" onClick={() => resetCountryRuleFee(country, level)} className="btn-reset" title="Use tier/base fee">↺</button>
                            )}
                          </div>
                          <select
                            value={mode}
                            disabled={!canEdit}
                            onChange={(e) => updateCountryRule(country, level, { mode: e.target.value })}
                            style={{
                              width: '100%',
                              fontSize: '0.68rem',
                              padding: '6px 4px',
                              borderRadius: '4px',
                              background: '#0f172a',
                              color: modeMeta.color,
                              border: `1px solid ${modeMeta.color}`,
                              fontWeight: 700,
                            }}
                          >
                            {MODE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {mode === REGISTRATION_MODES.PARTNER && (
                            <input
                              type="text"
                              placeholder="Partner name"
                              value={cell.partnerName || ''}
                              disabled={!canEdit}
                              onChange={(e) => updateCountryRule(country, level, { partnerName: e.target.value })}
                              style={{
                                width: '100%',
                                marginTop: '6px',
                                fontSize: '0.68rem',
                                padding: '5px 6px',
                                borderRadius: '4px',
                                background: '#0f172a',
                                border: '1px solid #d97706',
                                color: '#fde68a',
                              }}
                            />
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
        </>
        )}
      </section>

      {scalingModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Scale Matrix Pricing?</h3>
            <p>You've adjusted the Base Rate for <strong>{scalingModal.level}</strong> from {scalingModal.oldVal} to {scalingModal.newVal}. How should this ripple through your manual overrides?</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '25px' }}>
              <button onClick={() => handleScalingChoice('RESET')} className="btn-secondary">Reset All Overrides</button>
              <button onClick={() => handleScalingChoice('SCALE')} className="btn-activate">Scale Overrides Proportionally</button>
            </div>
            <button onClick={() => setScalingModal(null)} style={{ background: 'none', border: 'none', color: 'white', opacity: 0.5, marginTop: '20px', cursor: 'pointer' }}>Cancel Change</button>
          </div>
        </div>
      )}

      <style>{`
        .pricing-accordion-trigger {
          display: flex;
          align-items: center;
          gap: 16px;
          width: 100%;
          padding: 0;
          margin: 0;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
        }
        .pricing-accordion-trigger:hover span:first-child { text-decoration: underline; }
        .pricing-section--accordion { padding-bottom: 18px; }
        .pricing-section { background: rgba(0,0,0,0.2); border-radius: 12px; padding: 25px; border: 1px solid rgba(255,255,255,0.05); }
        .pricing-table { width: 100%; border-collapse: collapse; }
        .pricing-table th { text-align: left; padding: 12px; font-size: 0.7rem; text-transform: uppercase; color: #94a3b8; border-bottom: 1px solid #334155; }
        .pricing-table td { padding: 12px; border-bottom: 1px solid rgba(255,255,255,0.03); color: white; }
        .cell-overridden input { color: #138a8a !important; font-weight: bold; }
        .input-with-reset { display: flex; align-items: center; background: #0f172a; border: 1px solid #334155; border-radius: 4px; padding: 0 8px; }
        .currency-symbol { font-size: 0.7rem; opacity: 0.4; margin-right: 5px; }
        .pricing-input { background: transparent; border: none; color: white; padding: 8px 0; width: 100%; outline: none; font-size: 0.9rem; }
        .btn-commit { background: #138a8a; border: none; color: white; cursor: pointer; padding: 2px 6px; border-radius: 4px; font-size: 0.8rem; margin-left: 5px; }
        .btn-reset { background: none; border: none; color: #138a8a; cursor: pointer; padding: 0 5px; font-size: 1rem; }
        .tier-tag { font-size: 0.6rem; background: rgba(19, 138, 138, 0.2); color: #138a8a; padding: 2px 6px; border-radius: 4px; margin-left: 8px; }
        .admin-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: center; z-index: 2000; }
        .admin-modal { background: #1e293b; padding: 40px; border-radius: 12px; max-width: 500px; text-align: center; color: white; border: 1px solid #138a8a; }
      `}</style>
    </div>
  );
};

export default AdminPricing;